import Http from './http'
export class User {
	acl_permissions = {
		roles: [],
		permissions: [],
		modules: [],
	}
	constructor(data) {
		Object.assign(this, data)
		this.first_name = this.nickname || this.nick_name || this.username || this.first_name || this.name?.split(' ')[0]
		return this
	}
}
export class AuthService {
	static _instance = null
	#user = null
	/**
	 * @param {{refresh: boolean}} options
	 * @returns {Promise<boolean>}
	 */
	constructor() {
		AuthService._instance = this
	}

	isAuthenticated(options = { refresh: false }) {
		if (this.#user && !options.refresh) {
			return Promise.resolve(true)
		}
		return this.authenticate()
	}

	authenticate() {
		return new Promise(resolve => {
			var xhr = Http.get('/auth/user')
			xhr.then(response => {
				if (response.status !== 200) {
					resolve(false)
				}
				// Auth endpoints may return either the user directly or JSON:API-style
				// { data: { ...user } }. Normalize both shapes before resolving the
				// nickname used by the header and personal workspace.
				let user = new User(response.data?.data || response.data)
				if (user.acl_permissions.roles.length === 0 && user.acl_permissions.modules.length === 0) {
					document.write('<h1>You are not authorized to access this page</h1>')
					return
				}
				Object.freeze(user)
				this.#user = user

				resolve(true)
				// eslint-disable-next-line no-unused-vars
			}).catch(_error => {
				resolve(false)
			})
		})
	}
	/**
	 * @returns {User}
	 */
	getUser() {
		if (!this.#user) {
			throw new Error('User is not authenticated')
		}
		return this.#user
	}
}

/**
 * Returns the best dashboard route for the current user based on their module access.
 * Falls back to error-403 if no module is accessible.
 *
 * @returns {string} route name
 */
export function getDefaultRoute() {
	const dashboardsByModule = [
		{ module: 'MGN,admin', route: 'network-dashboard' },
		{ module: 'MGN,network-systems', route: 'network-dashboard' },
		{ module: 'MGAG,marketing', route: 'agency-dashboard' },
		{ module: 'MGTV,streamers', route: 'tv-dashboard' },
		{ module: 'MGC,community', route: 'community-dashboard' },
		{ module: 'MGJ,application', route: 'jobs-dashboard' },
		{ module: 'MGAI,profiles', route: 'ai-dashboard' },
		{ module: 'MGS,categories', route: 'shop-dashboard' },
		{ module: 'MGA,courses', route: 'academy-dashboard' },
		{ module: 'MGCL,franchise-models', route: 'club-dashboard' },
		{ module: 'MGT,website', route: 'token-dashboard' },
		{ module: 'MGV,dashboard', route: 'ventures-dashboard' },
		{ module: 'MGAG,podcasts', route: 'agency-podcasts-calendar' },
	]

	let user = null
	try {
		const service = AuthService._instance
		if (!service) return 'error-403'
		user = service.getUser()
	} catch (e) {
		return 'error-403'
	}

	if (!user || !user.acl_permissions) {
		return 'error-403'
	}

	// Admin master goes to network dashboard
	if (user.acl_permissions.roles.includes('MGN,admin_master')) {
		return 'network-dashboard'
	}

	const userModules = user.acl_permissions.modules || []

	for (const entry of dashboardsByModule) {
		if (userModules.includes(entry.module)) {
			return entry.route
		}
	}

	return 'error-403'
}

export function canNavigate(roles = null, app = null, module = null) {
	/**
	 * @type {User|null}
	 */
	let user = null

	try {
		const service = app && app.$authService ? app.$authService : (this && this.$authService ? this.$authService : null)
		if (!service) {
			return false
		}
		user = service.getUser()
	} catch (e) {
		return false
	}

	if (!user || !user.acl_permissions) {
		return false
	}

	const isAdminMaster = user.acl_permissions.roles.indexOf('MGN,admin_master') > -1

	// Admin master bypasses everything
	if (isAdminMaster) {
		return true
	}

	// Check roles
	if (roles) {
		if (!Array.isArray(roles)) {
			roles = [roles]
		}

		let hasRole = false
		for (const role of roles) {
			const system = role.split(',')[0]
			if (!roles.includes(`${system},admin`)) {
				roles.push(`${system},admin`)
			}
			if (user.acl_permissions.roles.includes(role)) {
				hasRole = true
				break
			}
		}
		if (!hasRole) {
			return false
		}
	}

	// Check module access
	if (module) {
		const system = module.split(',')[0]
		const isPlatformAdmin = user.acl_permissions.roles && user.acl_permissions.roles.includes(`${system},admin`)
		const userModules = user.acl_permissions.modules || []
		if (!isPlatformAdmin && !userModules.includes(module)) {
			return false
		}
	}

	return true
}
